import React from "react";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Hidden from "@material-ui/core/Hidden";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Paper from "@material-ui/core/Paper";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import {makeStyles} from "@material-ui/core/styles";

import DownloadIcon from '@material-ui/icons/CloudDownload';
import MessageIcon from '@material-ui/icons/Message';
import MovieIcon from '@material-ui/icons/Movie';
import PeopleIcon from '@material-ui/icons/People';
import TwitterIcon from '@material-ui/icons/Twitter';
import VisibilityIcon from '@material-ui/icons/Visibility';

import TimeAgo from "react-timeago/lib";
import clsx from 'clsx';
import htmlToImage from 'html-to-image';
import {TwitterTweetEmbed} from 'react-twitter-embed';
import {useParams} from "react-router-dom";

import {Brand, brandStyleKey} from "../Brand";
import {ChatPost} from "../components/Post";
import {ChatSubscriber, stageClient, UIDivider, UIDividerType, UILineItem} from "../logic/StageClient";
import {SpeakerAvatar} from "../components/SpeakerAvatar";
import {downloadTxt} from "../logic/Downloader";
import {notificationSoundOn} from "../logic/Persistence";
import {playMessageSound} from "../logic/AudioPlayer";
import {tweetReply, tweetText} from "../logic/ShareExternally";


const useLocalStyles = makeStyles(theme => ({
  // import all of the Brand styles here too (mainly colored bars)
  ...Brand.styles,
  centerVertical: {
    marginTop: 'auto',
    marginBottom: 'auto',
  },
  chatPaper: {
    marginTop: '3em',   // for the toolbar
    minHeight: '30vh',  // for looks
    marginBottom: '-32px',
    paddingTop: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    borderRadius: 15,
    boxShadow: '0 -4px 12px -2px rgba(156, 39, 176, 0.35),  0 8px 18px -2px rgba(176, 39, 102, 0.38)',
  },
  bottomPadding: {
    height: '64px',
  },
  // the purple top bar with stats
  chatTopBar: {
    color: Brand.primaryColor,
    marginTop: '-10px', // -2em
    minHeight: '3em',
    boxShadow: 'none',
    backgroundColor: '#fffafd', // #ffe9f8
    [theme.breakpoints.up('sm')]: {
      backgroundColor: 'transparent',
    },
  },
  chatTopBarStats: {
    color: '#444',
  },
  chatTopBarSummary: {
    marginTop: 'auto',
    marginBottom: 'auto',
    textAlign: 'right',
  },
  // single line of chat
  chatLine: {
    [theme.breakpoints.down('xs')]: {
      paddingRight: '8px',
    },
  },
  chatLineOwner: {
    color: Brand.styles.appbar_ann_chef.backgroundColor,
  },
  // chatLine: speaker
  speakerAvatarImage: {
    marginRight: '16px',
    height: '48px',
    width: '48px',
    filter: 'drop-shadow(-1px 2px 1px #DDD)',
  },
  speakerAvatarImageTyping: {
    height: '36px',
    width: '36px',
    margin: '6px',
    marginRight: '22px', // 16 + 6
    opacity: '0.4',
  },
  // chatLine: tweet button
  hoverButton: {
    opacity: '0.1',
    transition: "opacity 300ms cubic-bezier(0.34, 1.61, 0.7, 1)",
    "&:hover": {
      opacity: '1',
    }
  },
  // chatLine "Tweet"
  twitterMessage: {
    minHeight: '200px', // placeholder for the min vertical space of a tweet - in our typical case.
    marginBottom: '10px',
    // marginTop: '-22px',
  },
  // chatLine "Spacer"
  spacerContainer: {
    display: 'flex',
    flexDirection: 'row',
    textAlign: 'center',
    margin: '32px auto 16px auto',
    maxWidth: '900px',
  },
  spacerExtended: {
    flexGrow: 0.5,
    margin: 'auto 8px',
    height: '7px',
    background: '#EEEEEE',
  },
  spacerText: {
    minWidth: '120px',
    padding: '8px 12px',
    margin: '20px 6px 4px 6px',
  },
  spacerTimeAgo: {
    color: '#CCCCCC',
  },
  spacerButton: {
    marginTop: 'auto',
    marginBottom: 'auto',
    color: '#CCCCCC',
  },
}));

// for the 'just now' or 'x minutes ago' in the stories
const minutesFormatter = (value, unit, suffix, s, next) => (unit === 'second' && (suffix === 'ago' || value === 0)) ? 'just now' : next();
const TimeAgoS = ({utcSeconds, className}) =>
  <TimeAgo date={utcSeconds * 1000} minPeriod={1} className={className} formatter={minutesFormatter}/>

// remember if scrolled to bottom when we get content updates
let wasScrolledToBottom = true;

interface ChatStateTypes {
  snapshot: StageTypes.ChatSnapshot | null,
  uiLines: UILineItem[],
  speaking: string,
}

const stateMerge = (newState) => (prevState) => Object.assign({}, prevState, newState);

export function ViewChat(/*props*/) {
  const classes = useLocalStyles();

  // this comes from the route declared with react-router, and it's the most important value
  const queryChatId = useParams()['id'];

  // note: using a single state holder to avoid multiple component re-creation when updating a bunch of state at once
  const [content, setContent] = React.useState<ChatStateTypes>({
    snapshot: null,
    uiLines: [],
    speaking: '',
  });
  const {snapshot, uiLines, speaking} = content;

  // preprocess
  let lastChatLine: StageTypes.IChatLine | undefined = undefined;
  let lastNewStory: UIDivider | undefined = undefined;
  for (let idx = uiLines.length - 1; idx >= 0; --idx) {
    const lineItem = uiLines[idx];
    if (!lastChatLine && lineItem.chatLine)
      lastChatLine = lineItem.chatLine;
    if (!lastNewStory && lineItem.divider && lineItem.divider.dividerType === UIDividerType.NewStory)
      lastNewStory = lineItem.divider;
    if (lastChatLine && lastNewStory)
      break;
  }

  // handle auto-scrolling to bottom (the first time, and subsequents)
  const isAtBottom = () => {
    // const isLastLineRef = content.length > 1 && chatLines.slice(-1)[0].ref;
    const snapMargin = 120 /*default snap*/ + 200 /*tweet expansion*/;
    return (window.innerHeight + window.scrollY + snapMargin) >= document.body.offsetHeight;
  }
  const messagesEndRef = React.useRef(null);
  React.useEffect(() => {
    if (messagesEndRef.current && wasScrolledToBottom) {
      // @ts-ignore
      messagesEndRef.current.scrollIntoView({behavior: "smooth"})
    }
  }, [content]);

  // pub-sub with stageClient, for this Chat information
  React.useEffect(() => {
    // handle updates from the subscribed object
    const chatSubscriber: ChatSubscriber = {
      onChatSnapshot: (chatSnapshot, uiLines: UILineItem[], typingSpeaker) => {
        setContent(stateMerge({
          snapshot: chatSnapshot,
          uiLines: uiLines,
          speaking: typingSpeaker,
        }));
      },
      onChatTyping: (speaker) => {
        wasScrolledToBottom = isAtBottom();
        setContent(stateMerge({
          speaking: speaker,
        }));
      },
      onChatUILines: (chatLines) => {
        wasScrolledToBottom = isAtBottom();
        setContent(stateMerge({
          uiLines: chatLines,
          speaking: '',
        }));
      },
    };
    stageClient.singleSubscribeToChatAndSwitchToIt(queryChatId, chatSubscriber);
    return () => {
      stageClient.singleUnsubscribeFromChat(queryChatId, chatSubscriber);
    }
  }, [queryChatId]);

  // experiment: keep the last story only1
  const adjUiLines = content.uiLines;
  // let adjChatLines: UILineItem[]/*: StageTypes.IChatLine[]*/ = content.uiLines.slice();
  // for (let i = adjChatLines.length - 1; i >= 0; --i) {
  //   if (adjChatLines[i]._isDivider) {
  //     adjChatLines = adjChatLines.slice(i + 1);
  //     break;
  //   }
  // }

  // for downloading the TXT of a story
  const storyTextFromUid = (uid: number | string, header?: string): string | null => {
    const uiStoryIdx = uiLines.findIndex(line => line.uid === uid) + 1;
    if ((uiStoryIdx - 1) === -1 || uiStoryIdx >= uiLines.length || !uiLines[uiStoryIdx].chatLine) {
      console.log('error downloading the story TXT');
      return null;
    }
    const storyId = uiLines[uiStoryIdx].chatLine.seg;
    const storyLines = uiLines.filter(line => line.chatLine && line.chatLine.seg === storyId);
    if (!storyLines.length) {
      console.log('error: empty story, nothing to download');
      return null;
    }
    return (header || '').replace('STORY_ID', '' + storyId) +
      `${storyLines.map(line => `${line.chatLine.speaker}: ${line.chatLine.line}`).join('\n')}\n`;
  }
  const downloadStoryAsTxt = (uid: number | string) => {
    const storyText = storyTextFromUid(uid, '=== Story STORY_ID ===\n');
    if (storyText && storyText.length > 10) downloadTxt(storyText, 'story.txt');
  }
  const tweetStoryContent = (uid: number | string) => {
    // Use the story, and if the text exceeds a constant, ellipse-ize it
    let storyText = storyTextFromUid(uid);
    if (storyText && storyText.length > 10) {
      const tweetLimit = 240;
      if (storyText.length > (tweetLimit - 3))
        storyText = storyText.slice(0, tweetLimit - 3) + '...';
      tweetText(storyText);
    }
  }

  // for replying to a tweet with a single line
  const tweetLineReply = (eventTarget: (EventTarget & HTMLButtonElement), uid: number | string) => {
    // Find the chatLine
    const replyLine = uiLines.find(line => line.uid === uid);
    if (!replyLine || !replyLine.chatLine) {
      console.log(`error replying with this line (${uid})`);
      return;
    }

    // format the reply tweet, and ellipse-ize extra content
    let replyText = `[${replyLine.chatLine.speaker} @askbrodown]: ${replyLine.chatLine.line} 🚨\n`;
    const tweetLimit = 240;
    if (replyText.length > (tweetLimit - 3))
      replyText = replyText.slice(0, tweetLimit - 3) + '...';

    // render tweet as image Blob
    htmlToImage.toBlob(eventTarget.parentElement.parentElement.parentElement, {
      filter: domNode => domNode.tagName !== "BUTTON",  // exclude the Tweet button
      backgroundColor: '#fff9ff', // linear-gradient(to bottom, #edb4f7, #f2c8fa 10%, #fff9ff 100%)
    })
      .then(blob => {
        // copy to Clipboard
        // @ts-ignore
        const clip = new ClipboardItem({'image/png': blob});
        // @ts-ignore
        navigator.clipboard.write([clip])
          .catch(error => console.log('Error writing to clipboard', error))
          .finally(() => {
            // send the Twitter reply
            const tweetId = eventTarget.dataset.reply_ref;
            tweetReply(tweetId, replyText);
          });
      })
      .catch(error => console.log('Error taking the screenshot', error));
  }

  // pre-process data and numbers
  // const clientMessages = chatLines.filter(line => !line._isDivider).length;
  // const lastUiChatLine = findLastChatLine(uiLines);
  const allMessages = lastChatLine ? lastChatLine.seq : (snapshot && snapshot.stage.lastSeq);
  const allSegments = lastChatLine ? lastChatLine.seg : (snapshot && snapshot.stage.lastSeg);

  const Sep = () => <span style={{color: 'rgba(255,255,255,0.5)', marginRight: '1em'}}/>;

  const brandStyleName = brandStyleKey(snapshot && snapshot.theme);
  const barColorClassName = classes[brandStyleName];

  const isOwner = (line) => snapshot && snapshot.announcer === 'chef' && line.speaker === 'Chef';
  const chatLineOwnerClassName = clsx(classes.chatLine, classes.chatLineOwner);

  const chatSoundOn = notificationSoundOn();

  // (kinda ugly) temporary running variable to carry the tweet id from the first line to the other of the story
  let lastTweetId: string;

  return (<React.Fragment>
    {/* Chat Explainer */}
    {snapshot && <ChatPost snapshot={snapshot} fixed={true}/>}

    {/* Chat View (paper with topbar and messages) */}
    <Container maxWidth="md" className={classes.centerVertical}>
      <Paper elevation={8} className={classes.chatPaper}>

        {/* Name & Stats bar */}
        <Toolbar className={classes.chatTopBar/*clsx(barColorClassName, classes.chatTopBar)*/}>
          <Grid container>
            {/* Chat Title (Topic) */}
            <Grid item xs={12} sm={5}>
              <Hidden xsDown implementation="css">
                <Typography component="h2" variant="h6" noWrap>
                  {snapshot && snapshot.topic}
                </Typography>
              </Hidden>
            </Grid>
            {/* Summary symbols */}
            <Grid item xs={12} sm={7} className={classes.chatTopBarSummary}>
              <PeopleIcon fontSize="small" aria-label="Users"/>
              &nbsp;<span className={classes.chatTopBarStats}>{snapshot && snapshot.users}</span><Sep/>
              <MessageIcon fontSize="small" aria-label="Messages"/>
              &nbsp;<span
              className={classes.chatTopBarStats}>{/*clientMessages*/}{allMessages && allMessages.toLocaleString()}</span><Sep/>
              <MovieIcon fontSize="small" aria-label="Stories"/>
              &nbsp;<span className={classes.chatTopBarStats}>{allSegments && allSegments.toLocaleString()}</span><Sep/>
              <VisibilityIcon fontSize="small" aria-label="Watching"/>
              &nbsp;<span className={classes.chatTopBarStats}>{snapshot && snapshot.stage.watchers}</span>
            </Grid>
          </Grid>
        </Toolbar>

        {/* List of all Chat Lines */}
        <List>
          {adjUiLines.map(uiLine => {
            // Divider
            if (uiLine.divider) {
              const divider = uiLine.divider;
              return (
                <Box key={uiLine.uid} className={classes.spacerContainer}>
                  <div className={classes.spacerExtended}/>
                  {/* Tweet story text */}
                  {divider.dividerType === UIDividerType.NewStory &&
                  <IconButton className={classes.spacerButton} onClick={() => tweetStoryContent(uiLine.uid)}>
                    <TwitterIcon/>
                  </IconButton>}
                  {/* Divider */}
                  <Box>
                    <Typography className={clsx(barColorClassName, classes.spacerText)}>
                      {/*{(divider._isFuture && !speaking) ? 'Countdown' : 'Next story'}*/}
                      {divider.dividerType === UIDividerType.FutureStory && 'Countdown'}
                      {divider.dividerType === UIDividerType.NewStory && (divider === lastNewStory ? 'New Story' : 'Next Story')}
                      {divider.dividerType === UIDividerType.LineSkip && '~~~'}
                    </Typography>
                    <TimeAgoS utcSeconds={uiLine.ts} className={classes.spacerTimeAgo}/>
                  </Box>
                  {/* Download Story Text */}
                  {divider.dividerType === UIDividerType.NewStory &&
                  <IconButton className={classes.spacerButton} onClick={() => downloadStoryAsTxt(uiLine.uid)}>
                    <DownloadIcon/>
                  </IconButton>}
                  {/* (future) Story fork */}
                  {/*<IconButton color="secondary" aria-label="tweet" component={RouterLink} to={'/story/fork/' + line.seg}/>*/}
                  <div className={classes.spacerExtended}/>
                </Box>
              );
            }
            // ChatLine
            if (uiLine.chatLine) {
              const chatLine = uiLine.chatLine;
              const chatLineRef = chatLine.ref;
              // Tweet: embed
              if (chatLineRef && (typeof chatLineRef === 'string') && chatLineRef.startsWith('tw-')) {
                lastTweetId = chatLineRef.slice(3).trim();
                return (
                  <Box key={uiLine.uid} className={classes.twitterMessage}>
                    <TwitterTweetEmbed tweetId={lastTweetId} options={{
                      // options: https://developer.twitter.com/en/docs/twitter-for-websites/embedded-tweets/guides/embedded-tweet-parameter-reference
                      width: 520,           // max, experimental
                      cards: 'hidden',      // don't load images, videos
                      conversation: 'none', // don't load conversation stuff
                      align: 'center',      // center
                      dnt: true,            // do-not-track to protect our users
                    }}/>
                    <div style={{display: "none"}}>{`${chatLine.speaker}: ${chatLine.line}`}</div>
                  </Box>
                )
              }
              // ChatLine: render
              return (
                <ListItem key={uiLine.uid} className={isOwner(chatLine) ? chatLineOwnerClassName : classes.chatLine}>
                  <SpeakerAvatar speaker={chatLine.speaker} className={classes.speakerAvatarImage}/>
                  <ListItemText primary={chatLine.line}
                                secondary={SpeakerAvatar.hasAvatar(chatLine.speaker) ? undefined : chatLine.speaker}/>
                  <Hidden xsDown implementation="css">
                    <ListItemIcon>
                      <IconButton size="medium" aria-label="tweet" color="primary" className={classes.hoverButton}
                                  data-reply_ref={lastTweetId} onClick={e => tweetLineReply(e.currentTarget, uiLine.uid)}>
                        <TwitterIcon/>
                      </IconButton>
                    </ListItemIcon>
                  </Hidden>
                  {(chatSoundOn && uiLine.chatLine === lastChatLine) && playMessageSound()}
                </ListItem>
              );
            }
            // uiItems: Error catch
            return (
              <div>???</div>
            );
          })}

          {/* Next is typing... */}
          {speaking && (
            <ListItem key="uid-next" className={classes.chatLine}>
              <SpeakerAvatar speaker={speaking} className={classes.speakerAvatarImageTyping}/>
              <ListItemText secondary={speaking + " is typing ..."}/>
            </ListItem>
          )}
        </List>

        {/* reference for the 'scroll down' */}
        <div ref={messagesEndRef} className={classes.bottomPadding}/>

        {/* with no uiItems, display that the chat is loading */}
        {adjUiLines.length === 0 && (
          <Box style={{textAlign: 'center', display: 'block'}}>
            <ListItemIcon><CircularProgress/></ListItemIcon>
            <ListItemText secondary="Loading chat ..."/>
          </Box>
        )}
      </Paper>
    </Container>
  </React.Fragment>);
}
